import { useContext, useLayoutEffect, useRef, useState } from 'react'
import { captureWriteIdentity, isWriteIdentityCurrent } from '@/core/api/http'
import { AutosaveContext } from './AutosaveBoundary'
import { Button, Icon, toastError } from 'admination-react'
import { useAudioRecorder } from '@/core/lib/audioRecorder'
import { transcribeAudio, type Dictation } from '@/core/lib/transcribe'
import { insertDictation, type DictationInsertion } from './dictation'

export function DictationButton({
  value,
  onChange,
  feld,
  kontext,
  onInserted,
  onBusyChange,
}: {
  value: string
  onChange: (v: string) => void
  feld?: string
  kontext?: string
  onInserted?: (insertion: DictationInsertion, dictation: Dictation) => void
  onBusyChange?: (busy: boolean) => void
}) {
  const recorder = useAudioRecorder()
  const boundary = useContext(AutosaveContext)
  const [busy, setBusyState] = useState(false)
  const committed = useRef<{
    value: string
    onChange: typeof onChange
    onInserted: typeof onInserted
    onBusyChange: typeof onBusyChange
    boundary: typeof boundary
  } | null>(null)
  useLayoutEffect(() => {
    committed.current = { value, onChange, onInserted, onBusyChange, boundary }
  })
  useLayoutEffect(() => () => {
    const target = committed.current
    committed.current = null
    target?.onBusyChange?.(false)
  }, [])
  const setBusy = (next: boolean) => {
    const target = committed.current
    if (!target) return
    setBusyState(next)
    target.onBusyChange?.(next)
  }

  async function onClick() {
    const identity = captureWriteIdentity()
    const active = () => {
      const target = committed.current
      return target?.boundary === boundary && isWriteIdentityCurrent(identity) ? target : null
    }
    if (recorder.recording) {
      setBusy(true)
      try {
        const blob = await recorder.stop()
        if (!active()) return
        const result = await transcribeAudio(blob, { feld, kontext })
        const target = active()
        if (!target) return
        if (!result.ok) {
          toastError(result.message)
          return
        }
        const text = result.data.text.trim()
        if (!text) return
        const insertion = insertDictation(target.value, text)
        target.onChange(insertion.next)
        target.onInserted?.(insertion, result.data)
      } finally {
        if (active()) setBusy(false)
      }
    } else {
      try {
        await recorder.start()
        if (!active()) await recorder.stop()
      } catch {
        if (active()) toastError("Mikrofon konnte nicht aktiviert werden.")
      }
    }
  }

  return (
    <Button
      type="button"
      variant="dictation"
      className={
        (recorder.recording ? 'is-recording' : '') + (busy ? ' is-processing' : '')
      }
      aria-label={recorder.recording ? "Aufnahme stoppen und einfügen" : "Diktieren"}
      title={busy ? "Wird transkribiert…" : recorder.recording ? "Aufnahme stoppen" : "Diktieren"}
      disabled={busy}
      onClick={onClick}
    >
      <Icon name="i-mic" />
    </Button>
  )
}
